import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API, formatError } from "@/lib/apiClient";
import { CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusPill, pillLabel } from "@/components/StatusPill";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, MessageCircle, Receipt, Copy, FileText, Upload, Edit3, MapPin } from "lucide-react";
import AdminDocumentEditor from "./AdminDocumentEditor";
import ChatThread from "@/components/ChatThread";
import { useCommentsPoll } from "@/lib/useCommentsPoll";
import { useScrollToHash } from "@/lib/useScrollToHash";
import CaseHistory from "./CaseHistory";
import FileRow from "@/components/FileRow";
import { nextStates } from "@/lib/lifecycle";
import { formatShortDate, txnGates, gateReason, AssignMenu, buildTrackingUrl } from "./lib";
import { ALL_TIME, isoToday } from "@/components/DateRange";
import { CollapsibleCard, CollapsiblePanel } from "@/components/CollapsibleSection";
import { fmtDateTime } from "@/lib/dates";

/**
 * One reference, with the copy button that is the whole point of showing it.
 *
 * Every value here gets read down a phone or pasted into an email, and three of the four are
 * uuids — retyping one by eye is how a support call ends up looking at the wrong payment. `full`
 * is what the button copies when the display value is a truncation: the customer's invoice number
 * IS the 8-character form, so that one copies what they can see, while an id shortened for
 * scanning copies whole.
 */
function RefCell({ label, value, full, hint, testid, onClick }) {
  if (!value) return null;
  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(full || value);
      toast.success(`${label} copied`);
    } catch { toast.error("Could not copy — clipboard unavailable"); }
  };
  return (
    <div className="min-w-0">
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6B7280]">{label}</div>
      <div className="flex items-center gap-2 mt-1 min-w-0">
        {onClick
          ? <button type="button" onClick={onClick} data-testid={testid}
                    className="font-mono text-xs text-[#A0AABF] hover:text-[#CBA96C] transition-colors truncate text-left">
              {value}
            </button>
          : <span data-testid={testid} className="font-mono text-xs text-[#A0AABF] truncate">{value}</span>}
        <button onClick={copy} title={`Copy ${label} — ${full || value}`} aria-label={`Copy ${label}`}
                data-testid={`${testid}-copy`}
                className="text-[#6B7280] hover:text-[#CBA96C] transition-colors shrink-0">
          <Copy className="w-3 h-3"/>
        </button>
      </div>
      {hint && <div className="text-[10px] text-[#6B7280] mt-0.5 truncate">{hint}</div>}
    </div>
  );
}

/**
 * The identifiers one purchase is known by, in one place.
 *
 * Four different strings name the same event and each belongs to a different party: the customer
 * has the **invoice number** (it is what their PDF prints), the bank statement carries the
 * **gateway reference**, our ledger keys on the **receipt id**, and the case itself is the
 * **transaction id**. Someone on a support call is handed one of the four and needs the others.
 *
 * The receipt id and the gateway reference are the same value while payments are mocked and will
 * diverge the day a real gateway is wired in — which is precisely the day the distinction starts
 * mattering — so both are shown rather than one standing in for the other.
 *
 * Transaction id renders even with no payment on record: a draft has one, and it is the id every
 * other tool in this admin is searched by.
 */
function ReferencesPanel({ t, pays }) {
  const nav = useNavigate();
  const list = pays || [];
  // Newest first from the server. A failed attempt is a row here too, so the successful payment is
  // the one to lead with — showing the latest attempt regardless would headline a failure on a
  // case that is paid for.
  const paid = list.find(p => p.status === "completed") || list[0] || null;
  const others = list.filter(p => p !== paid);

  return (
    <CollapsiblePanel id="case-references" title="References" testid="admin-txn-refs">
     <div className="px-5 pb-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <RefCell label="Transaction ID" value={t.id.slice(0, 8)} full={t.id} testid="ref-txn"/>
        <RefCell
          label="Invoice No" value={paid?.invoice_no} full={paid?.invoice_no} testid="ref-invoice"
          hint={paid?.covers > 1 ? `Covers ${paid.covers} documents` : null}
          // All-time window: this payment can be far older than whatever range the ledger was
          // last left on, and landing on an empty Payments page reads as a missing record.
          onClick={() => nav(`/admin/payments?q=${encodeURIComponent(paid.invoice_no)}`
                            + `&from=${ALL_TIME}&to=${isoToday()}`)}
        />
        <RefCell label="Receipt ID" value={paid?.id?.slice(0, 8)} full={paid?.id} testid="ref-receipt"
                 hint="Our ledger"/>
        <RefCell label="Gateway Ref" value={paid?.payment_ref?.slice(0, 12)} full={paid?.payment_ref}
                 testid="ref-gateway" hint={paid?.method ? `via ${paid.method}` : "PayU / gateway id"}/>
      </div>

      {/* Settlement is a separate event from the gateway saying yes — usually a day or two later
          and net of the fee — so it is stated only once it has actually happened. */}
      {paid?.reconciled && (
        <div className="mt-4 pt-3 border-t border-white/5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <RefCell label="Bank Ref / UTR" value={paid.bank_ref} full={paid.bank_ref} testid="ref-utr"
                   hint="Confirmed in bank"/>
          {paid.received_amount != null && (
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6B7280]">Net received</div>
              <div className="text-xs text-[#A0AABF] mt-1 font-mono">₹{Number(paid.received_amount).toLocaleString()}</div>
              <div className="text-[10px] text-[#6B7280] mt-0.5">of ₹{Number(paid.amount || 0).toLocaleString()} charged</div>
            </div>
          )}
        </div>
      )}

      {list.length === 0 && (
        <div className="text-xs text-[#6B7280] mt-3">
          No payment on record for this case yet.
        </div>
      )}

      {/* A case whose first attempt failed has a history worth seeing — the customer may well be
          calling about the charge that did not go through. */}
      {others.length > 0 && (
        <div className="mt-4 pt-3 border-t border-white/5 space-y-1" data-testid="admin-txn-refs-other">
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#6B7280] mb-1.5">
            Earlier attempts
          </div>
          {others.map(p => (
            <div key={p.id} className="flex items-center gap-3 text-xs text-[#6B7280] font-mono">
              <StatusPill status={p.status} dim="payment_status"/>
              <span className="truncate">{p.payment_ref}</span>
              <span className="ml-auto shrink-0">{fmtDateTime(p.ts)}</span>
            </div>
          ))}
        </div>
      )}
     </div>
    </CollapsiblePanel>
  );
}

/**
 * One line at the top of the workflow: whose move it is, and what that move is.
 *
 * The steps below name a *goal* each ("Legal review"), which is right for a checklist and wrong as
 * an answer to "what do I do now" — a step titled "Review completed" sitting above a status pill
 * reading "Not Started" reads as a contradiction rather than as a thing still to be done. This
 * says it in one sentence, and in particular says when the answer is "nothing, it is with someone
 * else" — which is most of the life of a case and was the one state the panel never expressed.
 */
function WorkflowNextLine({ t, g }) {
  const next = (() => {
    if (!g.assigned) return { mine: true, text: "Assign this case to yourself or an advocate." };
    if (!g.reviewDone) {
      if (g.selfReview) return { mine: true, text: "You are the reviewer — check the document, then mark the review completed." };
      if (t.review_status === "submitted") return { mine: true, text: "The advocate has submitted their review — accept it or return it for rework." };
      const who = t.reviewer_name || "the advocate";
      const started = t.review_status === "in_progress";
      return { mine: false, text: `${who} ${started ? "is reviewing this case" : "has not started the review yet"} — nothing for you to do until it is submitted.` };
    }
    if (g.infoMissing) return { mine: false, text: "Waiting on the customer for their contact details — fulfillment cannot start without them." };
    if (t.softcopy_status === "not_generated") return { mine: true, text: "The review is accepted — issue the soft copy, or skip it if this case ships by post only." };
    if (t.delivery_status === "not_started") {
      return { mine: true, text: t.softcopy_status === "waived"
        ? "Soft copy skipped — dispatch the hard copy when you are ready."
        : "Soft copy issued — dispatch the hard copy when you are ready." };
    }
    if (t.delivery_status === "dispatched") return { mine: false, text: "With the courier — record delivered or failed once you know." };
    if (t.status !== "completed") return { mine: true, text: "Delivered — mark the case completed." };
    return { mine: true, text: "Work done. Close the case when nothing more is expected on it." };
  })();
  return (
    <div data-testid="wf-next" className={`mb-4 rounded-lg px-3 py-2 text-sm border ${
      next.mine ? "border-[#CBA96C]/40 bg-[#CBA96C]/10 text-[#F9F8F6]"
                : "border-white/10 bg-white/[0.03] text-[#A0AABF]"}`}>
      <span className={`text-[10px] uppercase tracking-[0.18em] mr-2 ${next.mine ? "text-[#CBA96C]" : "text-[#6B7280]"}`}>
        {next.mine ? "Your move" : "Waiting"}
      </span>
      {next.text}
    </div>
  );
}

export function AdminTransactionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [comments, setComments] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [history, setHistory] = useState(null);
  const [reworkNote, setReworkNote] = useState("");
  const [showRework, setShowRework] = useState(false);
  const [closeNote, setCloseNote] = useState("");
  const [showClose, setShowClose] = useState(false);
  const [docOpen, setDocOpen] = useState(false);
  // everything admin can do from the queue is available here too, so a case can be driven
  // start-to-finish without bouncing back to the list
  const [reviewers, setReviewers] = useState([]);
  const [pays, setPays] = useState([]);
  const [deliveryState, setDeliveryState] = useState(null);
  // The per-case customer record admin can correct, and whether a reissue is being offered after
  // one. Kept separate from `deliveryState` because this is *who and where*, which the invoice also
  // depends on, while that dialog is about a parcel already in a courier's hands.
  const [customerEdit, setCustomerEdit] = useState(null);
  const [reissuing, setReissuing] = useState(false);
  const [offerReissue, setOfferReissue] = useState(false);
  // Step back through history rather than pushing /admin/transactions: pushing left the case page
  // *ahead* in the stack (so the browser's Back came straight back here) and dropped the queue's
  // search/tab/window, which the list now carries in its query string. Falls back to a plain
  // navigation when this page was opened directly and there is nothing to step back to.
  const goBack = () => {
    if (window.history.state?.idx > 0) nav(-1);
    else nav("/admin/transactions");
  };
  // Same control the customer's own case page has — admin reads this id down a phone or pastes
  // it into an email just as often, and the References panel's copy button (further down, for
  // the other three money references) is easy to miss for "just the transaction id".
  const copyTxnId = async () => {
    try { await navigator.clipboard.writeText(id); toast.success("Transaction ID copied"); }
    catch { toast.error("Could not copy — clipboard unavailable"); }
  };
  // Same reasoning as the transaction id above — admin pastes a customer's email or mobile into
  // an outgoing message or a search box just as often as they read the id, and retyping either by
  // eye off the screen is how a wrong number gets messaged.
  const copyText = async (label, value) => {
    try { await navigator.clipboard.writeText(value); toast.success(`${label} copied`); }
    catch { toast.error("Could not copy — clipboard unavailable"); }
  };
  const load = () => {
    api.get(`/transactions/${id}`).then(r => setT(r.data)).catch(e => setLoadError(e?.response?.data?.detail || "Failed to load transaction"));
    api.get(`/transactions/${id}/comments`).then(r => setComments(r.data)).catch(() => {});
    // The history is its own request: it lives in its own table now, and every admin action on
    // this page adds to it, so it reloads with everything else rather than going stale.
    api.get(`/admin/transactions/${id}/audit`).then(r => setHistory(r.data)).catch(() => {});
    // The money references. Its own request for the same reason the history is: it is a per-case
    // query the list routes deliberately do not carry. A case with no payment yet is a normal
    // state, not an error — the panel simply does not render.
    api.get(`/admin/transactions/${id}/payments`).then(r => setPays(r.data)).catch(() => {});
  };
  useEffect(load, [id]);
  // Live thread: without this, a message from the customer or advocate only appeared in either
  // pane after admin manually reloaded the page.
  useCommentsPoll(id, setComments);
  // Lands on the Messages card, not the top of the page, when arrived at via the queue's
  // message-count icons (`#case-messages` — see AdminTransactions.jsx::goToMessages).
  useScrollToHash(!!t);
  // Mark the case read once its page is actually open. Fire-and-forget: the read marker is a
  // convenience, and failing to record it must never surface as an error over a case that loaded
  // fine. Deliberately separate from `load` so a failed *load* does not mark an unread case read.
  useEffect(() => {
    if (!id) return;
    api.post(`/admin/transactions/${id}/viewed`).catch(() => {});
  }, [id]);
  useEffect(() => { api.get("/admin/reviewers").then(r => setReviewers(r.data)).catch(() => {}); }, []);
  // For the delivery dialog's partner picker (see below) — fetched once, same as reviewers.
  const [partners, setPartners] = useState([]);
  useEffect(() => { api.get("/admin/delivery-partners").then(r => setPartners(r.data)).catch(() => {}); }, []);
  const [reply, setReply] = useState("");
  const postComment = async () => {
    const val = reply.trim();
    if (!val) return;
    try {
      await api.post(`/transactions/${id}/comments`, { text: val });
      setReply(""); load(); toast.success("Sent");
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };
  const acceptReview = async () => {
    try { await api.put(`/admin/transactions/${id}/review-status`, { review_status: "accepted" }); toast.success("Review accepted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const requestRework = async () => {
    if (!reworkNote.trim()) { toast.error("A reason is required"); return; }
    try {
      await api.put(`/admin/transactions/${id}/review-status`, { review_status: "in_progress", note: reworkNote.trim() });
      toast.success("Returned to reviewer"); setReworkNote(""); setShowRework(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const applyFindings = async () => {
    try { await api.post(`/admin/transactions/${id}/apply-findings`); toast.success("Findings applied to the document"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const setReportVisibility = async (fid, visibility) => {
    try { await api.put(`/admin/transactions/${id}/reports/${fid}/visibility`, { visibility }); toast.success(visibility === "customer" ? "Shared with customer" : "Marked internal"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const setStatus = async (status) => {
    try { await api.put(`/admin/transactions/${id}/status`, { status }); toast.success("Marked completed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const assign = async (_tid, reviewer_id) => {
    try { await api.put(`/admin/transactions/${id}/assign`, { reviewer_id }); toast.success("Assigned to advocate"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const claimSelf = async () => {
    try { await api.put(`/admin/transactions/${id}/review`, { reviewed_by: "admin" }); toast.success("You are now handling this case"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const uploadSoftcopy = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/admin/transactions/${id}/deliver`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Soft copy uploaded + emailed"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { e.target.value = ""; }
  };
  // Some cases are fulfilled by physical delivery only — a digital copy was never going to be
  // issued, and leaving "Issue soft copy" sitting there as an outstanding to-do misrepresents the
  // case as stuck. Reversible: uploading one later (uploadSoftcopy above) still works from here.
  const waiveSoftcopy = async () => {
    if (!window.confirm("Skip issuing a digital soft copy for this case? You can still upload one later if that changes.")) return;
    try { await api.put(`/admin/transactions/${id}/softcopy/waive`); toast.success("Soft copy skipped — proceed with delivery"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const [adminUploading, setAdminUploading] = useState(false);
  // Admin (or the reviewer, from their own case page) attaching a document straight to the
  // customer's view — a draft to review, a form to fill, anything that isn't the final soft copy
  // (which has its own gated, audited "issue" step above) or an internal report. `kind: "document"`
  // plus a non-reviewer uploader is exactly what `POST /transactions/{tid}/upload` already treats
  // as customer-visible (routers/shared.py) — this was previously only reachable by the customer
  // themselves; admin had no upload control here at all.
  const uploadAdminDocument = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    setAdminUploading(true);
    const fd = new FormData(); fd.append("file", f); fd.append("kind", "document");
    try {
      await api.post(`/transactions/${id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded — visible to the customer");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setAdminUploading(false); e.target.value = ""; }
  };
    const saveDelivery = async () => {
    try {
      await api.put(`/admin/transactions/${id}/delivery`, deliveryState);
      toast.success(`Delivery → ${deliveryState.delivery_status}`);
      setDeliveryState(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const saveCustomer = async () => {
    // Only what admin actually changed goes up: the route leaves omitted fields alone precisely so
    // an untouched stale value cannot overwrite somebody else's correction.
    const sent = {};
    const snap = t.customer_snapshot || {};
    for (const f of ["name", "mobile", "country_code", "address"]) {
      if ((customerEdit[f] || "").trim() !== (snap[f] || "")) sent[f] = customerEdit[f].trim();
    }
    if (!Object.keys(sent).length) { setCustomerEdit(null); return; }
    if (customerEdit.note?.trim()) sent.note = customerEdit.note.trim();
    try {
      await api.put(`/admin/transactions/${id}/customer`, sent);
      toast.success("Details updated — the customer has been told");
      setCustomerEdit(null);
      // The invoice still says what it said when it was issued. Offering the reissue here rather
      // than doing it automatically is deliberate: it mints a tax document, and a corrected
      // spelling should not leave a revision trail behind it.
      if (t.order_id && sent.address) setOfferReissue(true);
      load();
    } catch (e) { toast.error(formatError(e?.response?.data?.detail) || "Update failed"); }
  };

  const reissue = async () => {
    setReissuing(true);
    try {
      const { data } = await api.post(`/admin/transactions/${id}/reissue-invoice`);
      toast.success(`Revised invoice ${data.invoice_no} issued and sent`);
      setOfferReissue(false);
      load();
    } catch (e) { toast.error(formatError(e?.response?.data?.detail) || "Reissue failed"); }
    finally { setReissuing(false); }
  };

  // closing is terminal and note-required — see LIFECYCLE.md "Closing a case"
  const closeCase = async () => {
    if (!closeNote.trim()) { toast.error("A closing note is required"); return; }
    try {
      await api.put(`/admin/transactions/${id}/status`, { status: "closed", note: closeNote.trim() });
      toast.success("Case closed"); setCloseNote(""); setShowClose(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Close failed"); }
  };

  if (loadError) return (
    <div className="space-y-4" data-testid="admin-txn-detail-error">
      <button onClick={goBack} className="inline-flex items-center gap-1.5 text-sm text-[#A0AABF] hover:text-white transition-colors">
        <ChevronLeft className="w-4 h-4" strokeWidth={1.5}/> Back
      </button>
      <div className="text-[#EF4444]">{formatError(loadError)}</div>
    </div>
  );
  if (!t) return <div className="text-[#A0AABF]" data-testid="admin-txn-detail-loading">Loading…</div>;
  const tok = localStorage.getItem("dt_token");
  // A terminal case is sealed — its attachments stop changing with it
  // (LIFECYCLE.md "Terminal cases are sealed").
  const locked = ["closed", "cancelled"].includes(t.status);
  const advocateReports = (t.reports || []).filter(r => r.kind !== "softcopy");
  // Only the current copy is shown — never a history of superseded ones. The customer's own page
  // already worked this way (LIFECYCLE.md "Current soft copy"); admin's list showing every prior
  // generation let the two disagree about how many soft copies exist on the same case.
  const allSoftcopies = (t.reports || []).filter(r => r.kind === "softcopy");
  const softcopyReports = t.current_softcopy_id
    ? allSoftcopies.filter(r => r.id === t.current_softcopy_id)
    : allSoftcopies.slice(-1);

  return (
    <div className="space-y-6" data-testid="admin-txn-detail">
      <button data-testid="admin-txn-back" onClick={goBack} className="inline-flex items-center gap-1.5 text-sm text-[#A0AABF] hover:text-white transition-colors">
        <ChevronLeft className="w-4 h-4" strokeWidth={1.5}/> Back
      </button>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#CBA96C] flex items-center gap-2">
            Transaction {t.id.slice(0,8)}
            <button
              data-testid="copy-txn-id" onClick={copyTxnId}
              title={`Copy full ID — ${t.id}`} aria-label="Copy transaction ID"
              className="text-[#6B7280] hover:text-[#CBA96C] transition-colors normal-case tracking-normal"
            ><Copy className="w-3 h-3"/></button>
          </div>
          <h1 className="font-serif-display text-3xl md:text-4xl text-white mt-1">
            {t.template_title || t.service_type.replace(/_/g,' ')}
          </h1>
          <div className="text-sm text-[#A0AABF] mt-1 flex items-center flex-wrap gap-x-1.5">
            <span>{t.customer_name}</span>
            {t.customer_email && (
              <span className="inline-flex items-center gap-1">
                · {t.customer_email}
                <button onClick={() => copyText("Email", t.customer_email)} data-testid="copy-customer-email"
                        title="Copy email" aria-label="Copy customer email"
                        className="text-[#6B7280] hover:text-[#CBA96C] transition-colors"><Copy className="w-3 h-3"/></button>
              </span>
            )}
            {t.customer_mobile && t.customer_mobile !== "-" && (
              <span className="inline-flex items-center gap-1">
                · {t.customer_mobile}
                <button onClick={() => copyText("Mobile number", t.customer_mobile)} data-testid="copy-customer-mobile"
                        title="Copy mobile number" aria-label="Copy customer mobile number"
                        className="text-[#6B7280] hover:text-[#CBA96C] transition-colors"><Copy className="w-3 h-3"/></button>
              </span>
            )}
          </div>
          {/* Where this parcel goes, and the one place to change it. The details are frozen on the
              case at payment (backend/core/customer_record.py) rather than read off the profile, so
              editing them here redirects *this* parcel — editing the customer's address book used
              to be the only way, and that silently re-pointed every other open case of theirs. */}
          <div className="text-xs text-[#A0AABF] mt-1.5 flex items-start gap-1.5"
               data-testid="admin-case-address">
            <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#6B7280]" strokeWidth={1.5}/>
            <span className="whitespace-pre-line">{t.customer_address || "No delivery address on file"}</span>
            {!locked && (
              <button onClick={() => setCustomerEdit({
                        name: t.customer_snapshot?.name || t.customer_name || "",
                        mobile: t.customer_snapshot?.mobile || "",
                        country_code: t.customer_snapshot?.country_code || "+91",
                        address: t.customer_snapshot?.address || t.customer_address || "",
                        note: "",
                      })}
                      data-testid="edit-case-customer" title="Change the details for this case"
                      className="text-[#6B7280] hover:text-[#CBA96C] transition-colors shrink-0 mt-0.5">
                <Edit3 className="w-3 h-3"/>
              </button>
            )}
          </div>
          <div className="text-xs text-[#6B7280] mt-1.5" data-testid="admin-txn-timestamps">
            Created {formatShortDate(t.created_at)} · Modified {formatShortDate(t.updated_at || t.created_at)}
          </div>
        </div>
        {/* Same top-of-page placement as the customer's own case page — the soft copy and the bill
            are what admin comes back to a case looking for, same as the customer, not something
            worth a scroll down to Document & Fulfillment to find (which no longer repeats them). */}
        <div className="flex items-center gap-2">
          {softcopyReports[0] ? (
            <a href={`${API}/files/${softcopyReports[0].id}?_t=${tok}`} target="_blank" rel="noopener noreferrer"
               data-testid="dl-softcopy-txn" title="Download soft copy">
              <Button variant="outline" size="sm"><FileText className="w-3.5 h-3.5 mr-1.5"/>Soft copy</Button>
            </a>
          ) : (
            <Button variant="outline" size="sm" disabled data-testid="dl-softcopy-txn"
              title="Not issued yet" className="text-[#6B7280] cursor-not-allowed">
              <FileText className="w-3.5 h-3.5 mr-1.5"/>Soft copy
            </Button>
          )}
          {t.payment_status === "completed" && t.order_id && (
            <a href={`${API}/orders/${t.order_id}/bill?_t=${tok}`} target="_blank" rel="noopener noreferrer"
               data-testid="dl-bill-txn" title="Download tax invoice">
              <Button variant="outline" size="sm"><Receipt className="w-3.5 h-3.5 mr-1.5"/>Bill</Button>
            </a>
          )}
        </div>
      </div>

      <CollapsiblePanel id="case-status" title="Case status" className="overflow-x-auto">
        <div className="grid grid-cols-6 gap-3 px-5 py-3 border-t border-white/5 text-[10px] tracking-[0.24em] uppercase text-[#6B7280] min-w-[660px]">
          <div>Status</div><div>Reviewed By</div><div>Review</div><div>Payment</div><div>Soft Copy</div><div>Delivery</div>
        </div>
        <div className="grid grid-cols-6 gap-3 px-5 py-4 items-center min-w-[660px]">
          <div><StatusPill status={t.status} dim="status"/></div>
          <div><StatusPill status={t.reviewed_by} dim="reviewed_by"/></div>
          <div><StatusPill status={t.review_status} dim="review_status"/></div>
          <div><StatusPill status={t.payment_status} dim="payment_status"/></div>
          <div><StatusPill status={t.softcopy_status} dim="softcopy_status"/></div>
          <div><StatusPill status={t.delivery_status} dim="delivery_status"/></div>
        </div>
      </CollapsiblePanel>

      {/* The three numbers a support call is conducted in. A customer quotes the invoice on their
          PDF; the bank statement shows the gateway's reference; our own records key on the
          transaction and receipt ids. They are four different strings for one purchase, and
          answering "which payment was this" used to mean leaving the case for the Payments page.
          Every one is copyable, because all four get pasted into an email or read down a phone. */}
      <ReferencesPanel t={t} pays={pays}/>

      {/* Sequential workflow — each stage unlocks only once the one before it is done, so the
          order of operations is visible rather than something admin has to remember. Mirrors the
          server-side gates exactly (LIFECYCLE.md "Gates"); the backend remains the enforcement. */}
      {/* also shown for `completed`, otherwise a completed case would offer no way to close it */}
      {["submitted", "completed"].includes(t.status) && (
        <CollapsibleCard id="case-workflow" title="Case workflow" testid="admin-workflow">
          <CardContent>
            {/* The panel's summary line. Admin opens a case asking one question — "is this mine to
                move, and if so what next?" — and used to have to read six steps to answer it. */}
            <WorkflowNextLine t={t} g={txnGates(t)} />
            <ol className="space-y-3">
              {(() => {
                // same gate helper the queue uses, so the two views can never disagree about
                // what's available (LIFECYCLE.md "The admin stage sequence")
                const g = txnGates(t);
                const { assigned, reviewDone, selfReview } = g;
                const issued = t.softcopy_status === "available";
                const waived = t.softcopy_status === "waived";
                const stages = [
                  {
                    key: "assign", n: 1, title: "Assign the case",
                    done: assigned, enabled: true, owner: assigned ? null : "admin",
                    detail: assigned ? (t.reviewer_name ? `Advocate — ${t.reviewer_name}` : "You (admin)") : "Not yet assigned",
                    hint: "Assign this case to yourself or an advocate before anything else.",
                    node: <AssignMenu t={t} reviewers={reviewers} onClaimSelf={claimSelf} onAssign={assign}/>,
                  },
                  {
                    key: "review", n: 2, title: "Legal review",
                    done: reviewDone, enabled: assigned,
                    // Whose move this is, which is the whole question the panel exists to answer.
                    // Once an advocate holds the case, admin has nothing to do until the advocate
                    // submits — saying so is more useful than an enabled-looking step with no action.
                    owner: reviewDone ? null
                      : !assigned ? "admin"
                      : selfReview ? "admin"
                      : t.review_status === "submitted" ? "admin"
                      : t.reviewer_name || "the advocate",
                    detail: reviewDone ? "Review accepted" : assigned
                      ? (selfReview ? "You are the reviewer on this case" : `Advocate's review: ${pillLabel(t.review_status, "review_status")}`)
                      : "Assign the case first",
                    hint: selfReview
                      ? "You took this case on yourself, so you own the review — mark it complete when you've checked the document."
                      : "The advocate submits their review; you accept it or return it for rework.",
                    action: g.canCompleteReview
                      ? { label: selfReview ? "Mark review completed" : "Accept review", onClick: acceptReview, testid: "wf-complete-review" }
                      : null,
                  },
                  {
                    key: "issue", n: 3, title: "Issue soft copy",
                    // Waiving resolves this rung the same way issuing does — it stops being an
                    // outstanding to-do either way, just by two different decisions.
                    done: issued || waived, enabled: g.canIssue,
                    owner: (issued || waived) ? null : g.canIssue ? "admin" : g.infoMissing ? "the customer" : null,
                    detail: issued ? "Soft copy issued to customer"
                      : waived ? "Skipped — fulfilling by physical delivery instead"
                      : g.canIssue ? "Ready to issue" : (gateReason(t, g, "issue") || "Not yet"),
                    hint: waived
                      ? "You chose not to issue a digital copy for this case. You can still upload one later if that changes."
                      : "Upload the executed document, or skip it if this case will be fulfilled by physical delivery only.",
                    // Upload, or explicitly skip.
                    //
                    // There used to be a Generate button here — and once a copy existed it read
                    // "Regenerate" — which re-rendered the PDF from the template. That is the wrong
                    // model for this deliverable: what the customer is buying is an executed
                    // document on stamp paper, not a fresh render, and a button that could silently
                    // replace a copy they had already downloaded made the issued artifact something
                    // that could change under them. Uploading is deliberate, and what is uploaded is
                    // what was actually signed.
                    //
                    // "Ignore soft copy" exists because not every case is fulfilled digitally — a
                    // case going out by post only doesn't need one, and without an explicit way to
                    // say so this rung looked permanently stuck on a channel admin never intended
                    // to use. Hidden once already issued (nothing to skip) or already waived
                    // (nothing more to say) — the upload control stays available either way, since
                    // admin can still issue one later.
                    node: g.canIssue ? (
                      <div className="flex items-center gap-2">
                        <label className="shrink-0">
                          <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.gif,.doc,.docx,.rtf,.txt,.zip" data-testid="wf-softcopy-file" onChange={uploadSoftcopy}/>
                          <Button size="sm" variant="outline" asChild>
                            <span className="cursor-pointer">{issued ? "Upload replacement" : "Upload soft copy"}</span>
                          </Button>
                        </label>
                        {!issued && !waived && (
                          <Button size="sm" variant="ghost" onClick={waiveSoftcopy} data-testid="wf-softcopy-waive"
                                  className="text-[#6B7280] hover:text-white">
                            Ignore soft copy
                          </Button>
                        )}
                      </div>
                    ) : null,
                  },
                  {
                    key: "delivery", n: 4, title: "Dispatch & delivery",
                    done: t.delivery_status === "delivered", enabled: g.canDispatch || t.delivery_status !== "not_started",
                    owner: t.delivery_status === "delivered" ? null
                      : t.delivery_status === "dispatched" ? "the courier"
                      : g.canDispatch ? "admin" : null,
                    detail: t.delivery_status === "not_started"
                      ? (g.canDispatch ? "Ready to dispatch" : (gateReason(t, g, "dispatch") || "Not yet"))
                      : pillLabel(t.delivery_status, "delivery_status"),
                    hint: "Hand to courier, then record delivered or failed.",
                    action: (g.canDispatch || t.delivery_status !== "not_started")
                      ? { label: "Update delivery", testid: "wf-delivery",
                          onClick: () => setDeliveryState({
                            delivery_status: t.delivery_status || "not_started",
                            courier_name: t.courier_name || "", tracking_id: t.tracking_id || "", tracking_url: t.tracking_url || "",
                            // Not resolved from courier_name — the stored fields are plain strings
                            // with no link back to a catalog row, so reopening the dialog always
                            // starts in "manual" state even if a partner was picked last time.
                            partner_id: null,
                          }) }
                      : null,
                  },
                  {
                    key: "complete", n: 5, title: "Mark completed",
                    done: t.status === "completed", enabled: g.canComplete || t.status === "completed",
                    owner: t.status === "completed" ? null : g.canComplete ? "admin" : null,
                    detail: t.status === "completed"
                      ? "Work done — case still open for questions"
                      : "The work is done — the case stays open for questions",
                    hint: "Mark completed once the customer genuinely has what they paid for.",
                    action: g.canComplete
                      ? { label: "Mark completed", onClick: () => setStatus("completed"), testid: "wf-complete" }
                      : null,
                  },
                  {
                    key: "close", n: 6, title: "Close the case",
                    done: false, enabled: true, owner: null,
                    detail: "Seals the file — no further messages, needs a note",
                    hint: "Closing is final. Do it once nothing more is expected on this case.",
                    action: { label: "Close case", onClick: () => setShowClose(true), testid: "wf-close" },
                  },
                ];
                // The step the case is actually sitting on: the first one not done that has been
                // unlocked. Everything after it is future work; everything before it is history.
                const current = stages.find(s => !s.done && s.enabled && s.key !== "close");
                return stages.map(s => {
                  const isCurrent = current && s.key === current.key;
                  const waitingOn = isCurrent && s.owner && s.owner !== "admin" ? s.owner : null;
                  return (
                  <li key={s.key} data-testid={`wf-stage-${s.key}`}
                      className={`flex items-start gap-3 rounded-lg -mx-2 px-2 py-1.5 ${s.enabled ? "" : "opacity-45"} ${
                        isCurrent ? "bg-white/[0.04] ring-1 ring-[#CBA96C]/30" : ""}`}>
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium shrink-0 mt-0.5 ${
                      s.done ? "bg-[#10B981] text-white" : s.enabled ? "bg-[#2D3B57] text-white" : "bg-white/10 text-[#6B7280]"
                    }`}>{s.done ? "✓" : s.n}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium flex flex-wrap items-center gap-2">
                        {s.title}
                        {/* A step can be "current" and still not be admin's to act on. Saying which
                            it is here is the difference between a checklist and an instruction. */}
                        {isCurrent && (waitingOn
                          ? <span data-testid={`wf-waiting-${s.key}`} className="text-[10px] uppercase tracking-[0.18em] text-[#F5A524] border border-[#F5A524]/40 rounded-full px-2 py-0.5">Waiting on {waitingOn}</span>
                          : <span data-testid={`wf-now-${s.key}`} className="text-[10px] uppercase tracking-[0.18em] text-[#080C16] bg-[#CBA96C] rounded-full px-2 py-0.5">Your move</span>)}
                      </div>
                      <div className="text-xs text-[#6B7280]">{s.detail}</div>
                      {s.enabled && !s.done && <div className="text-xs text-[#6B7280] mt-0.5">{s.hint}</div>}
                    </div>
                    {s.node}
                    {s.action && (
                      <Button data-testid={s.action.testid} size="sm" variant="outline"
                              onClick={s.action.onClick} className="shrink-0">{s.action.label}</Button>
                    )}
                  </li>
                  );
                });
              })()}
            </ol>
            {showClose && (
              <div className="space-y-2 mt-4 pt-4 border-t" data-testid="wf-close-box">
                {t.softcopy_status !== "available" && t.delivery_status !== "delivered" && (
                  <div className="text-xs text-[#F5A524] bg-[#F5A524]/10 border border-[#F5A524]/40 rounded p-2">
                    Neither fulfillment channel is complete. Closing is still allowed — just make sure that's intentional.
                  </div>
                )}
                <Label className="text-xs text-[#6B7280]">Closing note (required)</Label>
                <Textarea data-testid="wf-close-note" rows={2} value={closeNote} onChange={e => setCloseNote(e.target.value)}
                          placeholder="Why is this case being closed?"/>
                <div className="flex gap-2">
                  <Button data-testid="wf-close-confirm" onClick={closeCase} disabled={!closeNote.trim()}
                          className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190]">Close case</Button>
                  <Button variant="outline" onClick={() => { setShowClose(false); setCloseNote(""); }}>Cancel</Button>
                </div>
              </div>
            )}
          </CardContent>
        </CollapsibleCard>
      )}

      {/* The advocate's structured findings, and the accept/return decision that gates document
          issue. Before this, the review reached the document only if admin read a PDF and
          retyped it. See LIFECYCLE.md "Reviewer findings" / "Fulfillment follows review". */}
      {t.review_findings && (
        <CollapsibleCard
          id="case-review" testid="admin-review-findings"
          title={<>Advocate's Review <StatusPill status={t.review_status} dim="review_status"/></>}
        >
          <CardContent className="space-y-4 text-sm">
            <div>
              <div className="text-xs uppercase text-[#6B7280] tracking-widest mb-1">Summary</div>
              <div>{t.review_findings.summary || <span className="text-[#6B7280]">—</span>}</div>
            </div>
            {Object.keys(t.review_findings.form_data_corrections || {}).length > 0 && (
              <div>
                <div className="text-xs uppercase text-[#6B7280] tracking-widest mb-1">Proposed corrections</div>
                {Object.entries(t.review_findings.form_data_corrections).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-dashed py-1">
                    <span className="text-[#6B7280] capitalize">{k.replace(/_/g,' ')}</span>
                    <span className="text-right max-w-[60%]">
                      <span className="line-through text-[#6B7280] mr-2">{String((t.form_data || {})[k] ?? "")}</span>
                      <span className="font-medium">{String(v)}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
            {t.review_findings.recommended_clauses && (
              <div>
                <div className="text-xs uppercase text-[#6B7280] tracking-widest mb-1">Recommended clauses</div>
                <div className="whitespace-pre-wrap">{t.review_findings.recommended_clauses}</div>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
              <Button data-testid="apply-findings" variant="outline" onClick={applyFindings} disabled={t.review_findings.applied}>
                {t.review_findings.applied ? "Findings applied" : "Apply to document"}
              </Button>
              {t.review_status === "submitted" && (
                <>
                  <Button data-testid="accept-review" onClick={acceptReview} className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190]">Accept review</Button>
                  <Button data-testid="show-rework" variant="outline" onClick={() => setShowRework(v => !v)}>Return for rework</Button>
                </>
              )}
            </div>
            {showRework && t.review_status === "submitted" && (
              <div className="space-y-2">
                <Textarea data-testid="rework-note" rows={2} value={reworkNote} onChange={e => setReworkNote(e.target.value)}
                          placeholder="What does the advocate need to change? (required)"/>
                <Button data-testid="submit-rework" onClick={requestRework} disabled={!reworkNote.trim()}
                        className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190]">Send back to advocate</Button>
              </div>
            )}
          </CardContent>
        </CollapsibleCard>
      )}

      <div>
        {/* One card, not two — "Submission Summary" used to be its own card holding only a
            field-by-field dump (redundant with the document itself, now removed) and the
            "View / Edit document" button; folding it in here means one place for everything
            about the document itself, rather than a nearly-empty card next to the one that
            matters. Mirrors the same merge on the customer's own case page. */}
        <CollapsibleCard
          id="case-documents" title="Document & Fulfillment"
          /* Opens the document workbench — the customer's fields beside the same live preview
             they saw in the wizard. This is how a correction phoned in or messaged by the
             customer reaches the document, since their own fields lock once they pay. Rendered
             outside the collapse toggle so it stays reachable with the panel shut. */
          actions={
            <Button data-testid="open-doc-editor" variant="outline" size="sm" onClick={() => setDocOpen(true)}
                    title="View & edit the document, then download it to print">
              <FileText className="w-3.5 h-3.5 mr-1.5"/>View / Edit document
            </Button>
          }
        >
          <CardContent className="space-y-3 text-sm">
            <div>
              {/* Split by who attached it, same as the customer's own case page — a flat list
                  with the source named per-row still read as one pile once admin gained a second
                  way to add to it, and buried the upload control at the bottom where it looked
                  like it belonged to whichever section happened to be last. */}
              {(() => {
                const customerFiles = (t.documents || []).filter(f => f.uploader_role === "customer");
                const staffFiles = (t.documents || []).filter(f => f.uploader_role !== "customer");
                return (
                  <>
                    <div className="text-xs uppercase text-[#6B7280] tracking-widest mb-2">Customer uploads</div>
                    {customerFiles.length === 0 && <div className="text-[#6B7280]">No uploads.</div>}
                    {customerFiles.map(f => (
                      <div key={f.id} className="flex items-center gap-2">
                        {/* FileRow itself only ever offers delete on your *own* upload (DELETE
                            /files/{fid} enforces the same rule server-side) — admin sees a bin
                            next to a document admin uploaded, not next to the customer's. */}
                        <FileRow file={f} locked={locked} onDeleted={load} className="flex-1 hover:text-white"/>
                      </div>
                    ))}
                    <div className="text-xs uppercase text-[#6B7280] tracking-widest mb-2 mt-3 pt-3 border-t">Admin uploads</div>
                    {staffFiles.length === 0 && <div className="text-[#6B7280]">None yet.</div>}
                    {staffFiles.map(f => (
                      <div key={f.id} className="flex items-center gap-2">
                        <FileRow file={f} locked={locked} onDeleted={load} className="flex-1 hover:text-white"/>
                        {/* admin vs advocate — both land here, so the row still says which */}
                        <span className="text-[10px] text-[#6B7280] shrink-0 capitalize">{f.uploader_role === "reviewer" ? "advocate" : f.uploader_role}</span>
                      </div>
                    ))}
                    {/* Admin (or the reviewer, from their own case page) handing the customer
                        something to review — a draft, a form, anything short of the final soft
                        copy (its own gated "Issue" step above) or an internal report. Not gated on
                        review being accepted the way the customer's own upload is (LIFECYCLE.md —
                        that gate exists because a late customer document could no longer
                        influence a finished review; it never applied to admin/reviewer adding
                        something to it). Sits under "Uploaded for customer" — it adds to that
                        list, not to the customer's own. */}
                    {!locked && (
                      <label className="inline-flex items-center gap-2 mt-2 cursor-pointer">
                        <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.gif,.doc,.docx,.rtf,.txt,.zip"
                               data-testid="admin-upload-doc-file" onChange={uploadAdminDocument} disabled={adminUploading}/>
                        <Button size="sm" variant="outline" asChild>
                          <span><Upload className="w-3.5 h-3.5 mr-1.5"/>{adminUploading ? "Uploading…" : "Upload for customer"}</span>
                        </Button>
                      </label>
                    )}
                  </>
                );
              })()}
            </div>
            {advocateReports.length > 0 && (
            <div className="border-t pt-3">
              <div className="text-xs uppercase text-[#6B7280] tracking-widest mb-2">Advocate uploads</div>
              {advocateReports.map(f => {
                const shared = f.visibility === "customer";
                return (
                  <div key={f.id} className="flex items-center gap-2">
                    <FileRow file={f} locked={locked} onDeleted={load} className="flex-1 hover:text-white"/>
                    <button
                      data-testid={`toggle-vis-${f.id}`}
                      onClick={() => setReportVisibility(f.id, shared ? "internal" : "customer")}
                      title={shared ? "Visible to the customer — click to make internal" : "Internal only — click to share with the customer"}
                      className={`text-[10px] px-2 py-0.5 rounded-full border shrink-0 transition-colors ${shared ? "border-[#10B981]/40 text-[#34D399] bg-[#10B981]/10" : "border-[#6B7280]/30 text-[#6B7280] hover:border-white/30"}`}
                    >{shared ? "Shared" : "Internal"}</button>
                  </div>
                );
              })}
            </div>
            )}
            {t.delivery_status && t.delivery_status !== "not_started" && (
              <div className="border-t pt-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase text-[#6B7280] tracking-widest">Delivery</div>
                  {/* Deliberately available on a closed case too — unlike the workflow ladder
                      (hidden once closed, since "assign"/"issue"/"mark completed" are finished
                      questions there), a wrong courier name or tracking id is a data-entry
                      mistake, not more work on the case, and admin had no way to correct one once
                      the case closed. The backend agrees: set_delivery_status has no closed-case
                      guard — this control was the only thing missing. */}
                  <button
                    onClick={() => setDeliveryState({
                      delivery_status: t.delivery_status, courier_name: t.courier_name || "",
                      tracking_id: t.tracking_id || "", tracking_url: t.tracking_url || "", partner_id: null,
                    })}
                    data-testid="edit-delivery-details" title="Fix courier name or tracking id"
                    className="text-[#6B7280] hover:text-white transition-colors"
                  ><Edit3 className="w-3.5 h-3.5"/></button>
                </div>
                {t.courier_name && <div>Courier: {t.courier_name}</div>}
                {t.tracking_id && <div className="text-[#6B7280]">Tracking ID: {t.tracking_id}</div>}
                {t.tracking_url && <a href={t.tracking_url} target="_blank" rel="noopener noreferrer" className="text-white hover:underline">Track shipment →</a>}
              </div>
            )}
          </CardContent>
        </CollapsibleCard>
      </div>

      {/* One shared thread — customer, reviewer and admin are all members of the same case
          conversation, like a group chat (see backend/LIFECYCLE.md "Message and audit-log
          visibility"). There is no per-message release step any more and no addressed replies:
          one pane, one reply box, everyone with access to the case sees every message the instant
          it's posted. */}
      <CollapsibleCard
        id="case-messages" testid="admin-txn-messages"
        title={<span className="flex items-center gap-2"><MessageCircle className="w-4 h-4"/>Messages</span>}
        actions={<span className="text-xs text-[#6B7280]">{comments.length} total</span>}
      >
        <CardContent className="space-y-3">
          <div className="max-h-96 overflow-y-auto">
            <ChatThread comments={comments} viewerRole="admin"/>
          </div>
          <div className="flex gap-2 pt-3 border-t">
            <Textarea
              data-testid="admin-txn-comment-input"
              placeholder="Message the customer and reviewer…"
              value={reply}
              onChange={e => setReply(e.target.value)}
              rows={2}/>
            <Button
              data-testid="admin-txn-comment-send"
              onClick={postComment}
              className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190] self-end">Send</Button>
          </div>
        </CardContent>
      </CollapsibleCard>

      <CaseHistory history={history} />

      {/* Same delivery dialog the queue uses, so the courier details are captured identically
          wherever admin happens to be working */}
      {/* Correcting who and where, for this case only. The invoice is a separate, deliberate step
          offered afterwards — see `saveCustomer`. */}
      <Dialog open={!!customerEdit} onOpenChange={(o)=>!o && setCustomerEdit(null)}>
        <DialogContent data-testid="case-customer-dialog">
          <DialogHeader><DialogTitle>Customer details for this case</DialogTitle></DialogHeader>
          {customerEdit && (
            <div className="space-y-3">
              <p className="text-xs text-[#6B7280] leading-relaxed">
                These were frozen when the case was paid for. Changing them here redirects
                <strong className="text-white/80"> this case only</strong> and does not touch the
                customer's profile or any other case of theirs. The customer is told automatically.
              </p>
              <div>
                <Label className="text-xs text-[#6B7280]">Name</Label>
                <Input data-testid="case-customer-name" value={customerEdit.name} className="mt-1 h-9 text-sm"
                       onChange={e=>setCustomerEdit(s=>({...s, name: e.target.value}))}/>
              </div>
              <div className="flex gap-2">
                <div className="w-24 shrink-0">
                  <Label className="text-xs text-[#6B7280]">Code</Label>
                  <Input data-testid="case-customer-code" value={customerEdit.country_code} className="mt-1 h-9 text-sm"
                         onChange={e=>setCustomerEdit(s=>({...s, country_code: e.target.value}))}/>
                </div>
                <div className="flex-1">
                  <Label className="text-xs text-[#6B7280]">Mobile</Label>
                  {/* Digits only, as everywhere: this number is the password that opens the
                      customer's documents (backend/core/customer_record.py). */}
                  <Input data-testid="case-customer-mobile" value={customerEdit.mobile} inputMode="numeric"
                         className="mt-1 h-9 text-sm"
                         onChange={e=>setCustomerEdit(s=>({...s, mobile: e.target.value.replace(/\D/g, "")}))}/>
                </div>
              </div>
              <div>
                <Label className="text-xs text-[#6B7280]">Delivery address</Label>
                <Textarea data-testid="case-customer-address" value={customerEdit.address}
                          className="mt-1 text-sm min-h-[84px]"
                          onChange={e=>setCustomerEdit(s=>({...s, address: e.target.value}))}/>
              </div>
              <div>
                <Label className="text-xs text-[#6B7280]">Why (recorded on the case)</Label>
                <Input data-testid="case-customer-note" value={customerEdit.note} className="mt-1 h-9 text-sm"
                       placeholder="customer rang, has moved"
                       onChange={e=>setCustomerEdit(s=>({...s, note: e.target.value}))}/>
              </div>
              <div className="flex justify-end pt-2 border-t">
                <Button data-testid="case-customer-save" onClick={saveCustomer}
                        className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190]">Save and notify</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Offered, never automatic: a reissue mints a tax document. The original stays downloadable
          either way, so declining loses nothing. */}
      <Dialog open={offerReissue} onOpenChange={setOfferReissue}>
        <DialogContent data-testid="reissue-dialog">
          <DialogHeader><DialogTitle>Reissue the invoice?</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm text-[#A0AABF]">
            <p>
              Delivery is updated. The invoice still shows the address it was issued with — which is
              correct if the customer has simply moved since paying.
            </p>
            <p>
              Reissue only if the invoice itself was wrong. A revised invoice is a new document
              naming what it supersedes; the original is kept and stays downloadable, and the
              customer is emailed the revision.
            </p>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" data-testid="reissue-skip" onClick={()=>setOfferReissue(false)}
                      className="bg-transparent border-white/20 text-white hover:bg-white/5">
                Leave the invoice as issued
              </Button>
              <Button data-testid="reissue-confirm" onClick={reissue} disabled={reissuing}
                      className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190]">
                {reissuing ? "Reissuing…" : "Reissue invoice"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deliveryState} onOpenChange={(o)=>!o && setDeliveryState(null)}>
        <DialogContent data-testid="detail-delivery-dialog">
          <DialogHeader><DialogTitle>Delivery — {t.customer_name}</DialogTitle></DialogHeader>
          {deliveryState && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-[#6B7280]">Status</Label>
                <Select value={deliveryState.delivery_status} onValueChange={(v)=>setDeliveryState(s=>({...s, delivery_status: v}))}>
                  <SelectTrigger data-testid="detail-delivery-status" className="mt-1 h-9 text-sm"><SelectValue/></SelectTrigger>
                  <SelectContent>{nextStates("delivery_status", t.delivery_status || "not_started").map(s => <SelectItem key={s} value={s}>{pillLabel(s, "delivery_status")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {/* Marking a parcel delivered is a confirmation, not a data-entry step. The courier,
                  the tracking id and the URL were all recorded at dispatch, so re-presenting them
                  as empty-looking inputs invites admin to retype what is already correct — and a
                  blank submitted over a good value would overwrite it. They are shown read-only
                  instead, and submitted unchanged. Selecting "Dispatched" (still offered, since
                  `nextStates` includes the current state) brings the editable form back for a
                  correction. */}
              {deliveryState.delivery_status === "delivered" ? (
                <div className="rounded-lg border border-white/10 bg-[#080C16] p-3 space-y-2">
                  {[["Courier", deliveryState.courier_name, "courier"],
                    ["Tracking ID", deliveryState.tracking_id, "tracking-id"],
                    ["Tracking URL", deliveryState.tracking_url, "tracking-url"]].map(([label, value, slug]) => (
                    <div key={slug} className="flex gap-3 text-sm">
                      <span className="text-xs text-[#6B7280] w-24 shrink-0 pt-0.5">{label}</span>
                      <span data-testid={`detail-delivered-${slug}`} className="text-white/90 break-all">
                        {value || "—"}
                      </span>
                    </div>
                  ))}
                  <div className="text-[11px] text-[#6B7280] pt-1 border-t border-white/5">
                    Recorded at dispatch. Switch the status back to <span className="text-white/80">Dispatched</span> if
                    any of it needs correcting first.
                  </div>
                </div>
              ) : (
              <>
              {partners.length > 0 && (
                <div>
                  <Label className="text-xs text-[#6B7280]">Delivery partner</Label>
                  {/* A convenience, not a link the record depends on: picking one just pre-fills
                      courier name + tracking URL below from Settings → Delivery partners, and
                      every field stays directly editable afterward — the stored shape
                      (courier_name/tracking_id/tracking_url as plain strings) never changes. */}
                  <Select value={deliveryState.partner_id || ""} onValueChange={(pid) => {
                    const partner = partners.find(p => p.id === pid);
                    setDeliveryState(s => ({
                      ...s, partner_id: pid,
                      courier_name: partner ? partner.name : s.courier_name,
                      tracking_url: partner ? buildTrackingUrl(partner.url_template, s.tracking_id) : s.tracking_url,
                    }));
                  }}>
                    <SelectTrigger data-testid="detail-delivery-partner" className="mt-1 h-9 text-sm"><SelectValue placeholder="Type it in manually below, or pick one…"/></SelectTrigger>
                    <SelectContent>{partners.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs text-[#6B7280]">Courier name</Label>
                <Input data-testid="detail-courier" value={deliveryState.courier_name} onChange={e=>setDeliveryState(s=>({...s, courier_name: e.target.value}))} className="mt-1 h-9 text-sm"/>
              </div>
              <div>
                <Label className="text-xs text-[#6B7280]">Tracking ID</Label>
                {/* Recomputes the URL below on every keystroke when a partner is selected — typing
                    the id is usually the last thing admin does, and re-deriving the URL from it is
                    what actually makes picking a partner save any work. */}
                <Input data-testid="detail-tracking-id" value={deliveryState.tracking_id} onChange={e => {
                  const tracking_id = e.target.value;
                  const partner = partners.find(p => p.id === deliveryState.partner_id);
                  setDeliveryState(s => ({
                    ...s, tracking_id,
                    tracking_url: partner ? buildTrackingUrl(partner.url_template, tracking_id) : s.tracking_url,
                  }));
                }} className="mt-1 h-9 text-sm"/>
              </div>
              <div>
                <Label className="text-xs text-[#6B7280]">Tracking URL</Label>
                <Input data-testid="detail-tracking-url" value={deliveryState.tracking_url} onChange={e=>setDeliveryState(s=>({...s, tracking_url: e.target.value}))} className="mt-1 h-9 text-sm"/>
              </div>
              </>
              )}
              <div className="flex justify-end pt-2 border-t">
                <Button data-testid="detail-delivery-save" onClick={saveDelivery} className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190]">
                  {deliveryState.delivery_status === "delivered" ? "Mark delivered" : "Save"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reloads on save so Submission Summary immediately reflects the overrides */}
      <AdminDocumentEditor tid={id} open={docOpen} onOpenChange={setDocOpen} onSaved={load} />
    </div>
  );
}
